import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

function AnimatedCounter({ target, suffix = "", prefix = "" }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const duration = 2000;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, target]);

  return (
    <span ref={ref} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black">
      {prefix}{count.toLocaleString("it-IT")}{suffix}
    </span>
  );
}

const stats = [
  { target: null, fixed: "7.9 MLN", label: "Account Profilati" },
  { target: 20, suffix: "+", label: "Regioni Coperte", prefix: "" },
  { target: 98, suffix: "%", label: "Targettizzazione", prefix: "" },
  { target: 500, suffix: "+", label: "Segmenti Demografici", prefix: "" },
];

export default function StatsAboutSection() {
  return (
    <section id="chi-siamo" data-testid="stats-section" className="relative py-20 sm:py-28 lg:py-36 overflow-hidden">
      <div
        className="absolute inset-0 opacity-8"
        style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=1920&q=40)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-[#050505]/92" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-4"
        >
          <span className="font-mono text-[10px] sm:text-xs tracking-[0.3em] text-[#FF3366] uppercase">IL NOSTRO DATABASE</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3 sm:mt-4">
            Milioni di account profilati
          </h2>
          <p className="text-[#A3A3A3] font-body text-sm sm:text-base mt-3 max-w-xl mx-auto">
            Strada di Paderna, 2 Domagnano — Centro Fiorina — Repubblica di San Marino
          </p>
        </motion.div>

        <div className="mt-10 sm:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              data-testid={`stat-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              viewport={{ once: true }}
              className="text-center border border-white/10 bg-[#121212]/50 backdrop-blur-sm p-4 sm:p-6 lg:p-8"
            >
              {stat.fixed ? (
                <span className="font-heading text-3xl sm:text-4xl lg:text-6xl font-black">{stat.fixed}</span>
              ) : (
                <AnimatedCounter target={stat.target} suffix={stat.suffix} prefix={stat.prefix} />
              )}
              <p className="font-mono text-[9px] sm:text-xs tracking-[0.15em] text-[#A3A3A3] mt-2 sm:mt-4 uppercase">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
